import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { useDefensePlansStore } from '../store/defensePlansStore';

/**
 * Keeps the signed-in owner's plans loaded on a screen that isn't the map.
 *
 * The map starts this subscription (useMapSubscriptions.ts), but a tab only
 * mounts once it's visited, so a page opened directly, like /funding, would
 * otherwise see no plans. `subscribeOwnPlan` is a no-op when the same uid is
 * already subscribed. On unmount this leaves the listener alone, since the
 * map may be relying on it, and lets go only when the user has changed.
 * Anonymous scouts own no plans, so they're skipped, as on the map.
 */
export function useOwnPlansSubscription(): boolean {
  const uid = useAuthStore((s) => s.user?.id);
  const isAnonymous = useAuthStore((s) => s.user?.isAnonymous);
  const loaded = useDefensePlansStore((s) => s.ownPlansLoaded);

  useEffect(() => {
    if (!uid || isAnonymous) return;
    useDefensePlansStore.getState().subscribeOwnPlan(uid);
    return () => {
      if (useAuthStore.getState().user?.id === uid) return;
      useDefensePlansStore.getState().unsubscribeOwnPlan();
    };
  }, [uid, isAnonymous]);

  return !!uid && !isAnonymous && loaded;
}
